/*
 * Copyright (c) 2026 GS Elektromedizinische Geraete G. Stemple GmbH, Germany - all rights reserved.
 * <p>
 * DO NOT ALTER OR REMOVE COPYRIGHT NOTICES OR THIS FILE HEADER.
 */

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import { TableContainer, Typography } from '@mui/material';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import React, { useContext, useEffect, useState } from 'react';
import TableHead from '@mui/material/TableHead';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import { ReportsAPIContext } from '../../../../provider/ReportsAPIProvider';
import _ from 'lodash';
import TableRow from '@mui/material/TableRow';
import { getFormattedTime } from '../../../../utils/util';
import { NodeType } from '../../../../backendModels/report.model';
import { trendsParameterValueToVitalParameterValueRounded } from '../../../../backendModels/trends.model';
import style from './VitalParameterImportDialog.scss?inline';
import { useCSS } from '../../../../provider/CSSProvider';
import { convertVitalParametersToMapping, VitalParametersMappingType } from '../../../../models/vitalParameters';
import { VitalParameterType } from '../../../../backendModels/vital-parameters.model';

export interface VitalParameterImportDialogProps {
  isDialogOpen: boolean;
  setIsDialogOpen: (isDialogOpen: boolean) => void;
  nodeType: NodeType;
}

const consideredVitalParameterTypes: VitalParameterType[] = ['HR', 'NIBP_SYS', 'NIBP_DIA', 'SPO2', 'CO2'];

export function VitalParameterImportDialog({
  isDialogOpen,
  setIsDialogOpen,
  nodeType,
}: VitalParameterImportDialogProps) {
  useCSS(style);

  const { adaptRecord, trends } = useContext(ReportsAPIContext);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [trendEntries, setTrendEntries] = useState<{ timestamp: number; parameters: VitalParametersMappingType }[]>([]);

  useEffect(() => {
    // Set this on close as well to avoid the disable animation on open
    setSelectedIndex(-1);

    if (isDialogOpen) {
      // Only fetch trend entries on open to prevent shifts while the dialog is open
      let newTrendEntries: { timestamp: number; parameters: VitalParametersMappingType }[] = [];
      for (const datasource of trends?.trends ?? []) {
        for (const datapoint of datasource.timeSeries) {
          newTrendEntries.push({
            timestamp: datapoint.timestamp,
            parameters: convertVitalParametersToMapping(
              datapoint.values.map((value) => trendsParameterValueToVitalParameterValueRounded(value))
            ),
          });
        }
      }

      // Only show entries that contain at least one value of interest
      newTrendEntries = newTrendEntries.filter((entry) =>
        Object.keys(entry.parameters).some((type) => consideredVitalParameterTypes.includes(type as VitalParameterType))
      );

      setTrendEntries(_.orderBy(newTrendEntries, (entry) => entry.timestamp, 'desc'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDialogOpen]);

  const confirmSelection = () => {
    adaptRecord('vitalParameters', nodeType, (draft) => {
      const selectedParameters = trendEntries[selectedIndex].parameters;

      for (const type of consideredVitalParameterTypes) {
        if (selectedParameters[type] != null) {
          _.remove(draft.vitalParameters, (parameter) => parameter.type === type);
          draft.vitalParameters.push(selectedParameters[type]);
        }
      }
    });

    setIsDialogOpen(false);
  };

  return (
    <Dialog
      open={isDialogOpen}
      onClose={() => setIsDialogOpen(false)}
      className='vitalparameter-import-dialog'
      closeAfterTransition={false}>
      <DialogTitle>Daten auswählen</DialogTitle>
      <DialogContent>
        <div>
          <TableContainer className='trends-table-container'>
            {trendEntries.length > 0 ? (
              <Table className='trends-table' stickyHeader={true}>
                <TableHead>
                  <TableCell>Zeitpunkt</TableCell>
                  <TableCell>
                    Puls <span className='unit'>1/min</span>
                  </TableCell>
                  <TableCell>
                    NIBP <span className='unit'>mmHg</span>
                  </TableCell>
                  <TableCell>
                    SpO<sub>2</sub> <span className='unit'>%</span>
                  </TableCell>
                  <TableCell>
                    etCO<sub>2</sub> <span className='unit'>mmHg</span>
                  </TableCell>
                </TableHead>
                <TableBody>
                  {trendEntries.map((entry, index) => {
                    return (
                      <TableRow key={index} selected={selectedIndex === index} onClick={() => setSelectedIndex(index)}>
                        <TableCell>{getFormattedTime(entry.timestamp)}</TableCell>
                        <TableCell>{entry.parameters.HR?.value ?? '-'}</TableCell>
                        <TableCell>
                          {entry.parameters.NIBP_SYS?.value ?? '-'} / {entry.parameters.NIBP_DIA?.value ?? '-'}
                        </TableCell>
                        <TableCell>{entry.parameters.SPO2?.value ?? '-'}</TableCell>
                        <TableCell>{entry.parameters.CO2?.value ?? '-'}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              <Typography>
                Es wurden noch keine Messwerte erfasst. Stellen Sie sicher, dass das Gerät in corpuls Mission verknüpft
                ist.
              </Typography>
            )}
          </TableContainer>
        </div>
      </DialogContent>
      <DialogActions>
        <Button className='cancel-button' variant='outlined' onClick={() => setIsDialogOpen(false)}>
          Abbrechen
        </Button>
        <Button variant='contained' disabled={selectedIndex < 0} onClick={() => confirmSelection()}>
          Importieren
        </Button>
      </DialogActions>
    </Dialog>
  );
}
